<template>
    <header class="header" role="banner" itemscope itemtype="http://schema.org/WPHeader"
        :class="{ 'menu-opened': menuOpened }">
        <div class="inner-header">
            <slot name="logo"></slot>

            <div class="nav-wrapper">
                <div class="nav-aside">
                    <div class="btn-close" @click="closeMenu">
                        <span class="icon-cross"></span>
                    </div>

                    <div class="langs-wrapper">
                        <CustomSelect :max-height="1000" :options="langs" :model-value="selectedLang"
                            @update:model-value="selectLang" :allow-empty="false"></CustomSelect>

                        <div class="btn-close-submenu" @click="closeSubmenu" :class="{ 'shown': submenuOpened !== false }">
                            <span class="arrow"></span>
                        </div>
                    </div>
                </div>

                <nav class="nav-header" role="navigation" itemscope itemtype="http://schema.org/SiteNavigationElement">
                    <ul id="menu-header" class="menu">
                        <li v-for="menuItem in menu"
                            :key="menuItem.ID" 
                            :id="'menu-item-' + menuItem.ID"
                            :class="[
                                {'current-menu-item': menuItem.current || menuItem.children?.some(c => c.current)},
                                {'menu-item-has-children': menuItem.children},
                                {'submenu-shown': menuItem.ID === submenuOpened},
                                ...menuItem.classes]"
                            @mouseenter="overMenu(menuItem.ID, $event)"
                            @mouseleave.self="leaveMenu($event)">
                            <a :href="menuItem.url" @click="clickMenu(menuItem.ID, $event)">
                                {{ menuItem.title }}
                                <div class="btn-arrow">
                                    <div class="arrow arrow-right"></div>
                                </div>
                            </a>
                            <ul class="sub-menu" v-if="menuItem.children">
                                <li v-for="subMenuItem in menuItem.children"
                                    :key="subMenuItem.ID"
                                    :id="'menu-item-' + subMenuItem.ID"
                                    :class="[{'current-menu-item': subMenuItem.current}, ...subMenuItem.classes]">
                                    <a :href="subMenuItem.url.indexOf('#') === 0 ? menuItem.url + subMenuItem.url : subMenuItem.url" :data-menuhash="subMenuItem.hash" @click="onClickSubMenuLink">{{ subMenuItem.title }}</a>
                                </li>
                            </ul>
                        </li>
                    </ul>
                </nav>

                <a :href="bookingLink" class="btn btn-blue btn-reserver" target="_blank">
                    <span>{{ $filters.translate('reserver') }}</span>
                </a>
            </div>

            <slot name="call"></slot>

            <div class="btn-menu" @click="openMenu">
                <div class="line"></div>
                <div class="line"></div>
                <div class="line"></div>
            </div>
        </div>
    </header>
</template>

<script setup>
import dayjs from 'dayjs';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { useStore } from 'vuex';
import { computed, inject, onBeforeUnmount, onMounted, ref } from 'vue';
import CustomSelect from '@/components/CustomSelect.vue';

gsap.registerPlugin(ScrollTrigger);

const props = defineProps({
    hotelSelected: {
        type: [Object, Boolean],
        default: false
    },
    langs: {
        type: Object,
        default: () => { }
    },
    menu: {
        type: Array,
        default: () => []
    },
    switcherLangSelected: {
        type: String,
        default: ''
    }
});

const store = useStore();

const selectedLang = ref('');
const headerScrollInstance = ref(null);

const bookingLink = computed(() => {
    let startDate = dayjs();
    let url = props.hotelSelected
        ? window.urlBaseBooking + window.bookingSettings['header_path_for_hotel'] + '?'
        : window.urlBaseBooking + window.bookingSettings['header_path'] + '?headvar=ok&';

    return url +
        'lingua_int=' + bookingLang.value +
        (!props.hotelSelected ? '&id_gruppo=' + configBookingLink.value.id_gruppo + '&dc_gruppo=' + configBookingLink.value.dc_gruppo : '') +
        '&id_stile=' + (props.hotelSelected ? props.hotelSelected['booking_engine'].id_stile : configBookingLink.value.id_stile) +
        '&dc=' + (props.hotelSelected ? props.hotelSelected['booking_engine'].dc : '') +
        '&id_albergo=' + (props.hotelSelected ? props.hotelSelected['booking_engine'].id_albergo : '') +
        '&gg=' + startDate.format('D') +
        '&mm=' + startDate.format('M') +
        '&aa=' + startDate.format('YYYY') +
        '&notti_1=1' +
        '&tot_adulti=1' +
        '&tot_bambini=0' +
        '&tot_camere=1';
});

const submenuOpened = computed(() => store.state.submenuOpened);
const menuOpened = computed(() => store.state.menuOpened);
const configBookingLink = computed(() => store.state.configBookingLink);
const bookingLang = computed(() => store.getters.bookingLang);

const setMenuClicked = () => store.commit('setMenuClicked');
const openSubmenu = (menuId) => store.commit('openSubmenu', menuId);
const closeSubmenu = () => store.commit('closeSubmenu');
const openMenu = () => store.commit('openMenu');
const closeMenu = () => store.dispatch('closeMenu');

const clickMenu = (menuId, e) => {
    setMenuClicked()
    let menuItem = findMenuItem(menuId);
    if (menuItem.children?.length > 0) {
        if (menuItem.ID !== submenuOpened.value) {
            e.preventDefault()
        }

        if (e.target.closest('.btn-arrow') || menuItem.current) {
            e.preventDefault()
            e.stopPropagation()

            openSubmenu(menuId)
        }
    }
};

const overMenu = (menuId, e) => {
    let menuItem = findMenuItem(menuId);
    if (menuItem.children?.length > 0 && !menuOpened.value) {
        openSubmenu(menuId);
    }
};

const onClickSubMenuLink = (e) => {
    const hash = e.target.dataset.menuhash;

    if (hash) {
        scrollToHash(hash);
    }
};

const scrollToHash = (hash) => {
    if (hash && hash !== '') {
        let anchorElement = document.querySelector(
            '#' + hash + '-anchor'
        );

        if (anchorElement) {
            let scrollTop =
                anchorElement.getBoundingClientRect().top -
                document.querySelector('header.header')
                    .offsetHeight +
                window.scrollY;

            setTimeout(() => {
                gsap.to(window, {
                    scrollTo: { y: scrollTop },
                    duration: 0.65,
                    ease: 'power2.inOut',
                });
            }, 0);
        }
    }
};

const leaveMenu = (e) => {
    closeSubmenu()
};

const findMenuItem = (menuId) => {
    return props.menu.find(m => m.ID === menuId);
};

const selectLang = (lang) => {
    selectedLang.value = lang.value;
    document.location.href = selectedLang.value
}

onMounted(() => {
    selectedLang.value = props.switcherLangSelected;

    setTimeout(() => {
        headerScrollInstance.value = ScrollTrigger.create({
            trigger: 'header.header',
            endTrigger: 'footer.footer',
            start: 'top top-=1px',
            end: 'bottom top',
            toggleClass: 'scrolled'
        });
    }, 100);
})

onBeforeUnmount(() => {
    closeSubmenu();

    if (headerScrollInstance.value) {
        headerScrollInstance.value.kill();
        headerScrollInstance.value = null;
    }
});
</script>